/**
 * designBandOverlay.js — dual-color scene encoding (validated against Plotly 3.0.3)
 *
 * Each scene wedge shows TWO judgments at once:
 *   - inner CORE  = execution score  → this is Plotly's own wedge fill (we leave
 *                                      a hole in the overlay so it shows through)
 *   - BORDER all around = design score → a thin uniform frame we paint on top
 *
 * Plus heavy black separator rings at every level boundary (movie|act,
 * act|sequence, sequence|scene, and the outer edge of the scene ring).
 *
 * Hard requirements honored:
 *   - NON-INTERACTIVE: every element gets inline `pointer-events:none`, so all
 *     clicks/hovers (incl. the double-click-to-zoom on acts/sequences) pass
 *     straight through to Plotly's wedge paths. Inline style is used (not just
 *     CSS) so it holds regardless of stylesheet load order.
 *   - RE-ALIGNS on every Plotly redraw (caller re-invokes on plotly_afterplot).
 *   - Pure visual overlay: only appends into its own <g>, mutates no Plotly node.
 *
 * Matching: slices are matched to nodes by id read from `slice.__data__` (the
 * D3-bound hierarchy node), NOT by DOM index — Plotly renders slices
 * breadth-first (all acts, then sequences, then scenes), so index != data order.
 * `slice.__data__` also exposes `depth` (Movie=0, act=1, sequence=2, scene=3).
 */

import { gradeColors } from './Constants';

const OVERLAY_CLASS = 'design-band-overlay';
const SVG_NS = 'http://www.w3.org/2000/svg';

// ── Frame sizing: ONE scale-invariant rule for any node (scene/sequence/act).
// Two independent measurements drive everything:
//   t = radial thickness ("height", constant per ring, grows on zoom)
//   w = angular arc width ("width", varies per scene, can be a sliver)
//
//   Above/below bands (inner+outer): bR = clamp(BAND_FRAC·t, BAND_MIN, BAND_MAX)
//       → height-driven; identical for every scene in a ring; bigger when zoomed.
//   Left/right strips (sides):       bA = clamp((w − CORE_MIN)/2, SIDE_MIN, bR)
//       → width-driven; wants to equal bR (uniform frame); capped at bR so sides
//         are never thicker than top/bottom; floored at SIDE_MIN so even a thin
//         scene keeps a sliver of design on its sides; reserves CORE_MIN for the
//         execution core. On a sub-(2·SIDE_MIN)px scene the core yields and the
//         scene reads as solid design (design is the signal we most want kept).
const BAND_FRAC = 0.13; // above/below thickness as a fraction of height (t)
const BAND_MIN  = 5;    // px
const BAND_MAX  = 16;   // px
const SIDE_MIN  = 2;    // px — side strips never go below this
const CORE_MIN  = 4;    // px — angular core reserved before the sides yield

// Scene divider: thinner than Plotly's level (act/sequence) dividers, which are
// width 2. Scenes are the deepest level, so their dividers must be the thinnest.
const SCENE_DIVIDER_PX = 1.5;
const RING_STROKE_PX = 5; // heavy black level-separator rings
// Scene wedges narrower than this (arc length at mid-radius, px) get their
// label HIDDEN: Plotly renders scene labels at a fixed 12px font, rotates
// them radially on thin wedges, and centers them on the wedge's visual
// center — which in an annulus is biased outward — so on slivers the tail
// crosses the outer arc and the separator ring paints over it ("sc 7" → a
// mangled stub under the border). A wedge that can't legibly hold its label
// shows none; hover and selection still identify the scene. Re-evaluated on
// every redraw, so zooming in brings labels back when there's room.
const MIN_LABEL_ARC_PX = 16;
const HIDDEN_LABEL_CLASS = 'dbo-hidden-label';
// Selection focus ring — a color OUTSIDE the red→green score ramp so it never
// reads as a score. Blue with a white halo so it pops on both red and green.
const SELECT_COLOR = '#1f6bff';
const SELECT_HALO  = '#ffffff';
const SELECT_PX    = 5;

// Level highlight — a LIGHTER treatment than the single focus ring, drawn on
// EVERY wedge of an active grain (e.g. all scenes when the Scenes rollup is the
// home view) so the writer sees which ring the rollup is about. Same blue
// family as the focus ring (so they read as "the same kind of attention") but
// thinner and translucent: a subtle tinted outline, not a bold ring.
const LEVEL_COLOR   = '#0a84ff';   // electric blue selection color (not a score hue)
const LEVEL_PX      = 3.5;          // bold enough to read on thin scene slivers
const LEVEL_OPACITY = 0.95;
const LEVEL_HALO    = '#ffffff';    // white halo behind the outline so it pops on any fill
const LEVEL_TINT_OPACITY = 0.10; // (unused — outline+halo, no fill wash)

// ── Read glyph (the 3rd channel: design = border, execution = fill, Read =
// glyph). Read is a soft taste signal (freshness/charge/grip), so it's encoded
// as a COARSE 3-tier MARK rather than a precise number, and as a SHAPE (not a
// 3rd ramp color) so it never competes with the design/execution colors:
//   read ≥ HIGH  → filled triangle pointing radially OUTWARD ("up" = strong)
//   read ≤ LOW   → hollow triangle pointing radially INWARD  ("down" = weak)
//   between      → small neutral dot (calm middle)
// Drawn in dark ink with a white halo so it reads on both red and green fills.
// Suppressed on wedges too small to hold it (same spirit as the min-arc label
// rule) so thin scene slivers don't get cluttered.
const READ_HIGH = 6.5;
const READ_LOW  = 4.5;
const GLYPH_INK   = '#1f2937'; // slate, off the score ramp
const GLYPH_HALO  = '#ffffff';
const GLYPH_MIN_ARC = 13;  // px arc width below which the glyph is hidden
const GLYPH_MIN_T   = 10;  // px radial thickness below which the glyph is hidden
const GLYPH_MIN_PX  = 4;   // smallest legible glyph half-size
const GLYPH_MAX_PX  = 11;  // cap so thick act/movie rings don't get a huge mark
const GLYPH_RADIAL_FRAC = 0.60; // place at 60% out along the wedge's thickness

/**
 * Public entry. Wrapped so it can NEVER throw out of a Plotly event handler
 * (an exception inside plotly_afterplot would abort Plotly's event dispatch and
 * break interactions like double-click-to-zoom).
 *
 * @param {HTMLElement} chartEl   the Plotly graph div
 * @param {Array} sceneNodes      entries: { id, nodeType, design_score } (others ignored)
 * @param {string} [selectedId]   id of the currently selected node, to focus-ring
 * @param {Array|Set} [levelIds]  ids of every wedge in an active grain, to soft-highlight
 */
export function renderDesignBands(chartEl, sceneNodes, selectedId, levelIds, dualEncode = false) {
  try {
    _render(chartEl, sceneNodes, selectedId, levelIds, dualEncode);
  } catch (e) {
    if (typeof console !== 'undefined') console.warn('designBandOverlay skipped:', e);
  }
}

function _render(chartEl, sceneNodes, selectedId, levelIds, dualEncode = false) {
  if (!chartEl) return;
  const root = chartEl.querySelector('g.sunburst');
  if (!root) return; // chart not drawn yet

  let overlay = root.querySelector(`.${OVERLAY_CLASS}`);
  if (overlay) {
    while (overlay.firstChild) overlay.removeChild(overlay.firstChild); // idempotent wipe
  } else {
    overlay = document.createElementNS(SVG_NS, 'g');
    overlay.setAttribute('class', OVERLAY_CLASS);
    overlay.style.pointerEvents = 'none';
    root.appendChild(overlay);
  }
  overlay.style.pointerEvents = 'none';

  // Restore labels hidden on a previous pass (min-arc rule below) BEFORE the
  // early return, so labels come back when scenes are toggled off/zoomed.
  root.querySelectorAll(`.${HIDDEN_LABEL_CLASS}`).forEach(el => {
    el.style.visibility = '';
    el.classList.remove(HIDDEN_LABEL_CLASS);
  });

  const slices = root.querySelectorAll('g.slice');
  if (slices.length === 0) return; // e.g. includeScenes toggled off

  // id -> design score, at EVERY grain (D·E·R: design encodes as a band/frame
  // on movie/act/sequence/scene alike). sceneIds stays scene-only because the
  // min-arc label rule below applies only to the thin scene wedges.
  const designById = Object.create(null);
  const readById = Object.create(null);
  const sceneIds = Object.create(null);
  const actLabelById = Object.create(null);   // id -> act label, for the rim annotation
  (sceneNodes || []).forEach(n => {
    if (!n || !n.id) return;
    if (n.nodeType === 'scene') sceneIds[n.id] = true;
    if (n.nodeType === 'act' && n.label) actLabelById[n.id] = n.label;
    if (n.design_score != null) designById[n.id] = n.design_score;
    if (n.read_score != null) readById[n.id] = n.read_score;
  });

  // Collect every distinct ring-boundary radius (depth-agnostic, so it stays
  // correct when zoomed — __data__.depth is relative to the zoom root, radii
  // are not). Each unique inner/outer radius becomes a separator ring.
  let center = null, selGeom = null;
  // Set of ids to soft-highlight (active grain's wedges). The selected node, if
  // any, is excluded — it already gets the bold single focus ring, so a level
  // tint under it would muddy that signal.
  const levelSet = Object.create(null);
  (Array.isArray(levelIds) || levelIds instanceof Set
    ? Array.from(levelIds) : []).forEach(id => { if (id != null && id !== selectedId) levelSet[id] = true; });
  const levelGeoms = [];   // geometry of each active-grain wedge — soft-highlighted last
  const actGeoms = [];   // { g, label } for each act ring slice — drawn around the rim last
  const radii = [];
  const addRadius = r => {
    if (r == null || !isFinite(r)) return;
    for (const x of radii) if (Math.abs(x - r) < 1.5) return; // cluster within 1.5px
    radii.push(r);
  };

  slices.forEach(slice => {
    const dd = slice.__data__;
    const id = dd && ((dd.data && dd.data.id) || dd.id);
    const path = slice.querySelector('path.surface') || slice.querySelector('path');
    if (!path) return;
    const g = parseSliceGeometry(path.getAttribute('d'));
    if (!g) return;

    if (!center) center = { cx: g.cx, cy: g.cy };
    addRadius(g.ri);
    addRadius(g.ro);
    if (selectedId != null && id === selectedId) selGeom = g; // remember for focus ring
    if (id != null && levelSet[id]) levelGeoms.push(g);        // remember for level highlight
    if (id != null && actLabelById[id]) actGeoms.push({ g, label: actLabelById[id] }); // remember for rim labels

    // Min-arc label rule: hide labels on scene wedges too thin to hold them.
    if (id != null && sceneIds[id]) {
      const arcMid = Math.abs(angleSpan(g)) * (g.ri + g.ro) / 2;
      if (arcMid < MIN_LABEL_ARC_PX) {
        const st = slice.querySelector('g.slicetext');
        if (st) {
          st.style.visibility = 'hidden';
          st.classList.add(HIDDEN_LABEL_CLASS);
        }
      }
    }

    // Read glyph REMOVED (2026-06-23, owner call): three simultaneous channels
    // (border+fill+glyph) made the wheel too hard to parse. The combined view is
    // now Design=border + Execution=fill only; Read is seen by selecting the Read
    // lens (fill = read score). readById is retained but no longer drawn.

    // Design frame for any grain (movie/act/sequence/scene) with a score.
    const design = id != null ? designById[id] : undefined;
    if (design == null) return;
    // skip degenerate slivers / the center node
    if (Math.abs(angleSpan(g)) < 0.005 && g.laf === 0) return;

    // Scale-invariant border, relative to the node's own dimensions.
    const t = g.ro - g.ri;                          // radial thickness ("height")
    const w = Math.abs(angleSpan(g)) * g.ro;        // angular arc width ("width")
    // The center is always bigger than its borders: cap each border at ¼ of the
    // wedge's dimension ⇒ center ≥ ½ ⇒ center is at least 2× each border. This
    // also stops narrow scenes from being swallowed by their own frame.
    const bR = Math.min(clamp(t * BAND_FRAC, BAND_MIN, BAND_MAX), t / 4); // above/below
    const bA = Math.min(clamp((w - CORE_MIN) / 2, SIDE_MIN, bR), w / 4);  // left/right

    // DESIGN-FRAME (the dual encoding: Design = border, Execution = fill) ONLY in Design & Execution
    // mode. In Triage / single-axis / lens modes the wedge must show its OWN metric colour at full
    // strength — same as the inner rings — so the outer ring doesn't read as a different palette.
    if (dualEncode) {
      const frameD = buildFrameWithHole(g, bR, bA);
      // Site grade color, opaque so the SAME score is ALWAYS the same colour.
      appendPath(overlay, frameD, colorForDesignScore(design), 'none', 'evenodd');
    }
    // ONE thin black outline of the wedge perimeter — the scene divider; thinner than the
    // level (sequence/act) dividers. (Always drawn so scene boundaries stay legible.)
    appendPath(overlay, sector(g, g.ri, g.ro), 'none', '#000', null, SCENE_DIVIDER_PX);
  });

  // Heavy black separator rings at every level boundary (drawn last, on top).
  if (center && radii.length) {
    radii.sort((a, b) => a - b);
    const maxR = radii[radii.length - 1];
    radii.forEach(r => {
      // inset the outermost ring by half the stroke so it isn't clipped at the edge
      const rr = r === maxR ? r - RING_STROKE_PX / 2 : r;
      appendRing(overlay, center.cx, center.cy, rr, RING_STROKE_PX);
    });
    // Reading-direction cue: a small rounded arrow just outside the outer ring,
    // starting above scene 1 (top) and curving clockwise → "start here, read this way".
    appendDirectionArrow(overlay, center.cx, center.cy, maxR);
    // (Act rim labels removed 2026-06 — owner call: the acts are already clearly
    // labelled, larger, on the inner ring; the outer duplicates were clutter.)
  }

  // Level highlight (drawn under the single focus ring): a subtle blue wash +
  // a thin translucent outline on every wedge of the active grain, so the whole
  // ring the rollup describes lights up. Lighter than the focus ring on purpose
  // — it marks a SET, not a selection. Uses the same sector/appendPath helpers.
  // OUTLINE ONLY — a thin blue outline marks the active grain's wedges as a SET. The old
  // fill WASH flooded the whole ring with blue and muddied the score colours (badly when
  // colouring by a single axis), so the fill tint is gone; the outline alone marks the set.
  levelGeoms.forEach(g => {
    if (g.ri > 2) {
      const face = sector(g, g.ri, g.ro);
      // white halo first, bold electric outline on top → unmistakable on any score colour
      appendPath(overlay, face, 'none', LEVEL_HALO, null, LEVEL_PX + 3);
      const line = appendPath(overlay, face, 'none', LEVEL_COLOR, null, LEVEL_PX);
      if (line) { line.setAttribute('stroke-opacity', LEVEL_OPACITY); line.classList.add('dbo-level-glow'); }
    }
  });

  // Selection focus ring (any level): white halo + a blue ring. Blue sits outside
  // the red→green ramp so it never reads as a score. The centre/root is a full
  // disc (ri≈0), so we ring it as a CIRCLE at its outer edge rather than a sector
  // (a sector path on a full disc is degenerate and wouldn't show).
  const ringCircle = (cx, cy, rr) => {
    appendRing(overlay, cx, cy, rr, SELECT_PX + 6, SELECT_HALO);
    const glow = appendRing(overlay, cx, cy, rr, SELECT_PX + 4, SELECT_COLOR);
    if (glow && glow.classList) glow.classList.add('dbo-select-glow');
    appendRing(overlay, cx, cy, rr, SELECT_PX, SELECT_COLOR);
  };
  if (selGeom && selGeom.ri > 2) {
    const outline = sector(selGeom, selGeom.ri, selGeom.ro);
    appendPath(overlay, outline, 'none', SELECT_HALO, null, SELECT_PX + 6);
    const glow = appendPath(overlay, outline, 'none', SELECT_COLOR, null, SELECT_PX + 4);
    if (glow && glow.classList) glow.classList.add('dbo-select-glow');
    appendPath(overlay, outline, 'none', SELECT_COLOR, null, SELECT_PX);
  } else if (selGeom) {
    ringCircle(selGeom.cx, selGeom.cy, selGeom.ro);
  } else if (selectedId != null && center && radii.length) {
    // The centre/root is a full disc, not an annular sector, so it never lands in
    // selGeom — ring the centre hole (smallest radius) when the root is selected.
    ringCircle(center.cx, center.cy, radii[0]);
  }
}

/* ───────────────────────── helpers ───────────────────────── */

function appendPath(overlay, d, fill, stroke, fillRule, strokeWidth) {
  const p = document.createElementNS(SVG_NS, 'path');
  p.setAttribute('d', d);
  p.setAttribute('fill', fill);
  p.setAttribute('stroke', stroke || 'none');
  if (stroke && stroke !== 'none') {
    p.setAttribute('stroke-width', strokeWidth != null ? strokeWidth : 1);
    p.setAttribute('stroke-linejoin', 'round');
    p.setAttribute('vector-effect', 'non-scaling-stroke');
  }
  if (fillRule) p.setAttribute('fill-rule', fillRule);
  p.setAttribute('shape-rendering', 'geometricPrecision');
  p.style.pointerEvents = 'none'; // inline — clicks must reach Plotly's wedge
  overlay.appendChild(p);
  return p;
}

// Reading-direction arrow: a short, slightly-rounded arc just OUTSIDE the outer
// ring, beginning above the top (where scene 1 starts) and sweeping clockwise to
// the upper-right, capped with an arrowhead — tells a first-time viewer the wheel
// reads clockwise from the top. Drawn navy over a white halo so it reads on the
// page; non-interactive like the rest of the overlay.
function appendDirectionArrow(overlay, cx, cy, R) {
  const COLOR = '#14536e', HALO = '#ffffff';
  const Ra = R + 13;                       // sit just outside the outer separator ring
  const deg = d => (d * Math.PI) / 180;    // screen coords: y is DOWN, so top = -90°, clockwise = increasing angle
  const a1 = deg(-84), a2 = deg(-40);      // from just RIGHT of top (above scene 1) → upper-right
  const pt = a => ({ x: cx + Ra * Math.cos(a), y: cy + Ra * Math.sin(a) });
  const p1 = pt(a1), p2 = pt(a2);
  // the arc (sweep flag 1 = clockwise on a y-down canvas), rounded caps
  const arc = `M${p1.x},${p1.y} A${Ra},${Ra} 0 0 1 ${p2.x},${p2.y}`;
  const halo = appendPath(overlay, arc, 'none', HALO, null, 6);
  if (halo) halo.setAttribute('stroke-linecap', 'round');
  const line = appendPath(overlay, arc, 'none', COLOR, null, 2.6);
  if (line) line.setAttribute('stroke-linecap', 'round');
  // arrowhead at p2, pointing along the clockwise tangent  d/da (cos,sin) = (-sin,cos)
  const tx = -Math.sin(a2), ty = Math.cos(a2);
  const back = { x: p2.x - tx * 9, y: p2.y - ty * 9 };
  const px = -ty, py = tx, hw = 5.5;
  const l = { x: back.x + px * hw, y: back.y + py * hw };
  const r = { x: back.x - px * hw, y: back.y - py * hw };
  const head = `M${p2.x},${p2.y} L${l.x},${l.y} L${r.x},${r.y} Z`;
  appendPath(overlay, head, HALO, HALO, null, 3.5);   // halo
  appendPath(overlay, head, COLOR, 'none');           // filled head
}

// Act labels around the rim. For each act ring slice, drop a short radial tick at
// the act's angular center just outside the outer ring, then the act's label as
// horizontal text (anchored by which side it's on, so it never goes upside-down).
// Navy over a white halo so it reads on the page; non-interactive.
function appendActRimLabels(overlay, center, R, acts) {
  const COLOR = '#14536e';
  const c = { x: center.cx, y: center.cy };
  acts.forEach(({ g, label }) => {
    if (!label) return;
    const vS = _norm(_sub(g.outerStart, c)), vE = _norm(_sub(g.outerEnd, c));
    const ang = _sang(vS, vE);
    const vMid = _rot(vS, ang / 2);                  // unit vector to the act's mid-angle
    // tick from just outside the ring outward
    const p1 = { x: c.x + vMid.x * (R + 3), y: c.y + vMid.y * (R + 3) };
    const p2 = { x: c.x + vMid.x * (R + 9), y: c.y + vMid.y * (R + 9) };
    const tick = appendPath(overlay, `M${p1.x},${p1.y} L${p2.x},${p2.y}`, 'none', COLOR, null, 1.5);
    if (tick) tick.setAttribute('stroke-linecap', 'round');
    // text just beyond the tick, anchored so it grows away from the wheel
    const tp = { x: c.x + vMid.x * (R + 13), y: c.y + vMid.y * (R + 13) };
    const anchor = vMid.x > 0.25 ? 'start' : (vMid.x < -0.25 ? 'end' : 'middle');
    appendRimText(overlay, tp.x, tp.y, label, anchor, COLOR);
  });
}

function appendRimText(overlay, x, y, str, anchor, fill) {
  const t = document.createElementNS(SVG_NS, 'text');
  t.setAttribute('x', x); t.setAttribute('y', y);
  t.setAttribute('text-anchor', anchor);
  t.setAttribute('dominant-baseline', 'middle');
  t.setAttribute('font-size', '12.5');
  t.setAttribute('font-weight', '700');
  t.setAttribute('font-family', 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif');
  t.setAttribute('fill', fill);
  t.setAttribute('stroke', '#ffffff');
  t.setAttribute('stroke-width', '3.5');
  t.setAttribute('stroke-linejoin', 'round');
  t.setAttribute('paint-order', 'stroke');     // halo behind the glyphs
  t.style.pointerEvents = 'none';
  t.textContent = str;
  overlay.appendChild(t);
  return t;
}

function appendRing(overlay, cx, cy, r, w, color = '#000') {
  const c = document.createElementNS(SVG_NS, 'circle');
  c.setAttribute('cx', cx); c.setAttribute('cy', cy); c.setAttribute('r', r);
  c.setAttribute('fill', 'none');
  c.setAttribute('stroke', color);
  c.setAttribute('stroke-width', w);
  c.style.pointerEvents = 'none';
  overlay.appendChild(c);
  return c;
}

function appendDisc(overlay, cx, cy, r, fill, stroke, sw) {
  const c = document.createElementNS(SVG_NS, 'circle');
  c.setAttribute('cx', cx); c.setAttribute('cy', cy); c.setAttribute('r', r);
  c.setAttribute('fill', fill || 'none');
  if (stroke && stroke !== 'none') { c.setAttribute('stroke', stroke); c.setAttribute('stroke-width', sw != null ? sw : 1); c.setAttribute('vector-effect', 'non-scaling-stroke'); }
  c.style.pointerEvents = 'none';
  overlay.appendChild(c);
}

// Read glyph: a coarse 3-tier MARK at the wedge's angular center, encoding the
// Read score as a SHAPE (off the score-color ramp). High = filled triangle
// pointing radially outward; low = hollow triangle pointing inward; middle =
// neutral dot. Drawn ink-over-white-halo so it reads on any fill color. Hidden
// on wedges too small to hold it.
function drawReadGlyph(overlay, g, read) {
  const t = g.ro - g.ri;                       // radial thickness
  const w = Math.abs(angleSpan(g)) * g.ro;     // angular arc width
  if (t < GLYPH_MIN_T || w < GLYPH_MIN_ARC) return;
  const size = clamp(Math.min(t, w) * 0.22, GLYPH_MIN_PX, GLYPH_MAX_PX);
  if (size < GLYPH_MIN_PX) return;

  const c = { x: g.cx, y: g.cy };
  const vS = _norm(_sub(g.outerStart, c));
  const vE = _norm(_sub(g.outerEnd, c));
  const ang = _sang(vS, vE);
  const vMid = _rot(vS, ang / 2);              // unit vector to the wedge's mid-angle
  const perp = { x: -vMid.y, y: vMid.x };
  const rGlyph = g.ri + t * GLYPH_RADIAL_FRAC;
  const P = { x: g.cx + vMid.x * rGlyph, y: g.cy + vMid.y * rGlyph };

  if (read >= READ_HIGH || read <= READ_LOW) {
    // Triangle. apexDir = outward (+vMid) for high, inward (−vMid) for low.
    const s = read >= READ_HIGH ? 1 : -1;
    const apex = { x: P.x + vMid.x * size * s, y: P.y + vMid.y * size * s };
    const bMid = { x: P.x - vMid.x * size * 0.5 * s, y: P.y - vMid.y * size * 0.5 * s };
    const cL = { x: bMid.x + perp.x * size * 0.85, y: bMid.y + perp.y * size * 0.85 };
    const cR = { x: bMid.x - perp.x * size * 0.85, y: bMid.y - perp.y * size * 0.85 };
    const d = `M${apex.x},${apex.y} L${cL.x},${cL.y} L${cR.x},${cR.y} Z`;
    appendPath(overlay, d, 'none', GLYPH_HALO, null, 3);          // white halo
    if (read >= READ_HIGH) appendPath(overlay, d, GLYPH_INK, 'none');         // filled
    else appendPath(overlay, d, 'none', GLYPH_INK, null, 1.8);    // hollow outline
  } else {
    // Neutral middle: a small dot.
    const r = Math.max(2, size * 0.45);
    appendDisc(overlay, P.x, P.y, r + 1, GLYPH_HALO, 'none');      // white halo
    appendDisc(overlay, P.x, P.y, r, GLYPH_INK, 'none');           // ink dot
  }
}

// Parse a Plotly 3.x sunburst slice path d into wedge geometry.
// Format: M innerStart L outerStart A ro,ro 0 laf sf outerEnd L innerEnd A ri,ri ... Z
function parseSliceGeometry(d) {
  if (!d) return null;
  const re = /([MLAZ])|(-?\d*\.?\d+(?:e-?\d+)?)/gi;
  const toks = [];
  let m;
  while ((m = re.exec(d))) toks.push(m[1] ? m[1].toUpperCase() : parseFloat(m[2]));
  const cmds = [];
  let i = 0;
  while (i < toks.length) {
    const c = toks[i++];
    if (c === 'M' || c === 'L') cmds.push({ c, pts: [toks[i++], toks[i++]] });
    else if (c === 'A') cmds.push({ c, pts: [toks[i++], toks[i++], toks[i++], toks[i++], toks[i++], toks[i++], toks[i++]] });
    else if (c === 'Z') cmds.push({ c });
  }
  const M = cmds.find(x => x.c === 'M');
  const arcs = cmds.filter(x => x.c === 'A');
  const lines = cmds.filter(x => x.c === 'L');
  if (!M || arcs.length < 2 || lines.length < 1) return null;
  const innerStart = { x: M.pts[0], y: M.pts[1] };
  const outerStart = { x: lines[0].pts[0], y: lines[0].pts[1] };
  const oArc = arcs[0];
  const ro = oArc.pts[0], laf = oArc.pts[3], sf = oArc.pts[4];
  const outerEnd = { x: oArc.pts[5], y: oArc.pts[6] };
  const ri = arcs[1].pts[0];
  if (!(ro > 0) || !(ri >= 0) || ro <= ri) return null;
  const dx = innerStart.x - outerStart.x, dy = innerStart.y - outerStart.y;
  const seg = Math.hypot(dx, dy);
  if (seg < 1e-6) return null;
  const ux = dx / seg, uy = dy / seg;
  return { cx: innerStart.x + ux * ri, cy: innerStart.y + uy * ri, ro, ri, outerStart, outerEnd, laf, sf };
}

// full-angle annular sector of this wedge between radii rA..rB
function sector(g, rA, rB) {
  const sc = (P, r) => ({ x: g.cx + (P.x - g.cx) * (r / g.ro), y: g.cy + (P.y - g.cy) * (r / g.ro) });
  const As = sc(g.outerStart, rB), Ae = sc(g.outerEnd, rB), Be = sc(g.outerEnd, rA), Bs = sc(g.outerStart, rA);
  const sfIn = g.sf ? 0 : 1;
  return `M${As.x},${As.y} A${rB},${rB} 0 ${g.laf} ${g.sf} ${Ae.x},${Ae.y} `
       + `L${Be.x},${Be.y} A${rA},${rA} 0 ${g.laf} ${sfIn} ${Bs.x},${Bs.y} Z`;
}

// vector helpers for the angularly-inset core
const _sub = (p, c) => ({ x: p.x - c.x, y: p.y - c.y });
const _norm = v => { const m = Math.hypot(v.x, v.y) || 1; return { x: v.x / m, y: v.y / m }; };
const _rot = (v, a) => ({ x: v.x * Math.cos(a) - v.y * Math.sin(a), y: v.x * Math.sin(a) + v.y * Math.cos(a) });
const _sang = (a, b) => Math.atan2(a.x * b.y - a.y * b.x, a.x * b.x + a.y * b.y);
function angleSpan(g) {
  const c = { x: g.cx, y: g.cy };
  return _sang(_norm(_sub(g.outerStart, c)), _norm(_sub(g.outerEnd, c)));
}
function sectorV(c, rA, rB, vS, vE, sf, angWidth) {
  const P = (v, r) => `${c.x + v.x * r},${c.y + v.y * r}`;
  const laf = Math.abs(angWidth) > Math.PI ? 1 : 0;
  return `M${P(vS, rB)} A${rB},${rB} 0 ${laf} ${sf} ${P(vE, rB)} `
       + `L${P(vE, rA)} A${rA},${rA} 0 ${laf} ${sf ? 0 : 1} ${P(vS, rA)} Z`;
}

// design frame = full wedge MINUS an inset core (uniform border on all 4 sides),
// expressed as one path with an even-odd hole so Plotly's execution fill shows
// through the core.
// Frame = full wedge minus an inset core. bR = radial inset (each of inner/outer
// band), bA = angular inset in px (each side strip; 0 ⇒ no side strips). Even-odd
// hole so the execution fill shows through the core.
function buildFrameWithHole(g, bR, bA) {
  // radial inset, kept under half the thickness so a core always survives radially
  const radial = Math.min(bR, (g.ro - g.ri) / 2 - 0.5);
  const coreRi = g.ri + radial, coreRo = g.ro - radial;
  const outer = sector(g, g.ri, g.ro);
  if (coreRo <= coreRi) return outer; // too thin radially → solid design wedge
  const c = { x: g.cx, y: g.cy };
  const vS = _norm(_sub(g.outerStart, c)), vE = _norm(_sub(g.outerEnd, c));
  const ang = _sang(vS, vE), dir = Math.sign(ang) || 1;
  const dth = bA / coreRo;                    // side strip as an angle
  if (2 * dth >= Math.abs(ang) - 1e-4) return outer; // no angular room → solid design
  const vS2 = _rot(vS, dir * dth), vE2 = _rot(vE, -dir * dth);
  const core = sectorV(c, coreRi, coreRo, vS2, vE2, g.sf, ang - 2 * dir * dth);
  return outer + ' ' + core;
}

// Use the site's canonical grade palette (gradeColors === the .grade1–.grade10
// CSS classes), keyed by the rounded design score, but FLATTENED over white to
// an opaque rgb. Flattening matches how those translucent grades already look
// on the white page, while guaranteeing the band can't pick up the execution
// color beneath it — so the same score is always the same color.
function colorForDesignScore(score) {
  const key = clamp(Math.round(score), 1, 10);
  return flattenOverWhite(gradeColors[key]);
}

function flattenOverWhite(rgba) {
  const m = /rgba?\(([^)]+)\)/i.exec(rgba || '');
  if (!m) return rgba;
  const p = m[1].split(',').map(s => parseFloat(s.trim()));
  const r = p[0], g = p[1], b = p[2], a = p.length > 3 ? p[3] : 1;
  const f = v => Math.round(v * a + 255 * (1 - a));
  return `rgb(${f(r)},${f(g)},${f(b)})`;
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}
